/** @module Specification/BOM/PartMaterials */

define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom",
	"dojo/dom-class",
	"csi",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/action",
	"csi/config",
	"csi/core/extendedPathService",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/def",
	"csi/def/Actions",
	"csi/def/Request",
	"csi/def/ViewRef",
	"csi/heading/_base",
	"csi/heading/Specs",
	"csi/html",
	"csi/image/_base",
	"csi/locale",
	"csi/prefs/_base",
	"csi/service/viewService",
	"csi/session",
	"csi/state",
	"csi/state/rootStore",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"../main",
	"./BOMSectionPushController",
	"./DeleteSectionController",
	"./EditSectionController",
	"./PartMaterialRequireSectionMixin",
	"./PlacementTable",
	"../SizeVariationParams",
	"Apparel/ProductSize/SizeSet",
	"Apparel/ProductType",
	"./BOMCustomViewOptionsStore",
	"./BOMOptionsTab",
	"SiteAdmin/DataSheetTable",
	"SiteAdmin/DataSheetTablePreferences",
	"Specification/Model/partMaterial",
	"csi/access/crews",
], function(declare, lang, dom, domClass, csi, storage, _, csiAction, config, {parseExtendedPath},
			ExtendedPath, Key, def, Actions, Request, ViewRef, csiHeading, Specs, html, csiImage,
			locale, csiPrefs, {collectSpecDependencyPaths}, session, state, {featureTogglesStore}, xml, Query, QueryProcessor,
			Specification,BOMSectionPushController, DeleteSectionController, EditSectionController, PartMaterialRequireSectionMixin,
			PlacementTable, {SIZE_PARAMS, SIZE_VARIATION_BOM_SIZES, SIZE_VARIATION_MAP},
			SizeSet, ProductType, BOMCustomViewOptionsStore, BOMOptionsTab, DataSheetTable,
			DataSheetTablePreferences, {partMaterialIsSpecial}, crews) {

	const ACTION_EDIT_SIZE_VARIATIONS = "EditPartMaterialSizes";
	const ACTION_UPDATE_WEIGHT_PER_BOM_QTY = "UpdateWeightPerBOMQtyFromMaterialAll";

	const ACTION_EDIT = "BomSectionEdit";
	const ACTION_PUSH = "BomSectionPush";
	const ACTION_DELETE = "BomSectionDelete";

	const STORAGE_SELECTED_SUPPLIERS = "PartMaterials/selectedProductSuppliers";
	const EXPOSE_BOM_SUPPLIER_ATTR = "ExposeBOMSupplierAttributes";

	const PRODUCT_SOURCE = "ProductSource";
	const IS_ASSORTMENT = "IsAssortment";
	const COLOR_SOURCES_ATTRIBUTE = "Child:ColorSources"
	const BOM_PARENT_PATH = "Child:__Parent__/Child:__Parent__";
	const COLOR_SOURCES_PATH = `${BOM_PARENT_PATH}/${COLOR_SOURCES_ATTRIBUTE}`;
	const COLOR_SOURCES_FROM_PRODUCT_SOURCING = "Child:ProductSourcing/Child:ProductSources/Child:ColorSources";

	const ADMIN_PRODUCT_SPEC = "Product Specification Administrator";
	const ADMIN_SITE = "SiteAdministrator";
	const ADMIN_TDS_BOM = "TDS - BOM Administrator";
	const EDITOR_PRODUCT_SPEC = "Product Specification Editor";
	const EDITOR_TDS_BOM = "TDS - BOM Editor";
	const NEW_STYLE_SOURCE_BOM_ROLES = [ADMIN_PRODUCT_SPEC, ADMIN_SITE, ADMIN_TDS_BOM, EDITOR_PRODUCT_SPEC,
		EDITOR_TDS_BOM, "TDS - BOM Viewer"];

	const _Preferences = declare(DataSheetTablePreferences, {
		constructor: function() {
			this.addCustomOption("compareToTheme", false, false);
			this.addCustomOption("displayColorImages", true, false, "DisplayColorImages");
			this.addCustomOption("displaySupplierCodes", false, false, "DisplaySupplierCodes");
		}
	});

	const _extendedPathMPP = parseExtendedPath("(PartMaterial)Child:FromMaterialPlanPlacement(MaterialPlanPlacement)");

	/**
	 * Return Spec for MaterialPlanPlacement attribute
	 * @param {Object} map - BOM to Theme mapping of attribute id to Spec
	 * @param {Context} ctx
	 * @param {string} idPM - PartMaterial attribute id
	 * @param {string} [idMPP] - MaterialPlanPlacement attribute id; if not specified, assumes it is same as idPM
	 */
	function _addBomToThemeMapping(map, ctx, idPM, idMPP) {
		const key = new Key({
			extendedPath: _extendedPathMPP,
			id: idMPP || idPM
		});

		const spec = ctx.view.specBuilder.getSpecForKey(key);
		if (spec) {
			map[idPM] = spec;
		} else {
			console.warn("Specification/BOM/PartMaterials: could not resolve data point for MaterialPlanPlacement." + key.id);
		}
	}

	/**
	 * Determine if given size is in the given list of urls; if 2d-size, then also check 1d sizes
	 * @param {cnl} sizeUrl
	 * @param {cnl[]} urls
	 * @param {ICache} cache
	 * @return {boolean}
	 */
	function _isSizeInCompareList(sizeUrl, urls, cache) {
		if (!urls) {
			return false;
		}

		if (urls.includes(sizeUrl)) {
			return true;
		}

		// if 2d size, then also check either dimension
		const size = cache.get(sizeUrl);
		if (size.IsTwoDim) {
			return urls.includes(size.Dimension1Size) || urls.includes(size.Dimension2Size);
		}

		return false;
	}

	return declare([PlacementTable, PartMaterialRequireSectionMixin], {
		allowSuspendInCustomViewPreview: true,
		compareOptions: ["bomActiveSizes", "bomActiveSizes2"],
		customViewOptionsComponent: BOMOptionsTab,
		customViewOptionsStore: BOMCustomViewOptionsStore,
		newActionsButtonResource: "Create",
		placeholderActions: ["BomSectionMenuSelect", "BomSectionMenuCreate"],
		prefClass: _Preferences,

		/** @property {string} activeProductSalesRegion - product sales region URL to use to filter list of PartMaterials to display. Default empty */
		activeProductSalesRegion: csi.EMPTY_URL,

		/** @property {string[]} bomActiveColors - list of product color URLs selected to display */
		bomActiveColors: null,

		/** @property {string[]} bomActiveSizes - list of product size URLs selected to display; if BOM sizes are 2d, then this will contain only 1st dimension sizes */
		bomActiveSizes: null,

		/** @property {string[]} bomActiveSizes2 - list of 2nd dimension product size URLs selected to display */
		bomActiveSizes2: null,

		/** @property {SizeSet} bomActiveSizeSet - set of sizes to display in BOM */
		bomActiveSizeSet: null,

		/** @property {string[]} bomAvailableColorUrls - if populated, then this overrides the full list of BOMProductColors to consider in the view */
		bomAvailableColorUrls: null,

		/** @property {string[]} bomAvailableSizeUrls - if populated, then this overrides the full list of BOMProductSizes to consider in the view */
		bomAvailableSizeUrls: null,

		/** @property {Nodes} bomColorStore - store containing list of available colors */
		bomColorStore: null,

		/** @property {string} bomSize1Name - name of 1st dimension sizes if they are 2d */
		bomSize1Name: null,

		/** @property {string} bomSize2Name - name of 2nd dimension sizes if they are 2d */
		bomSize2Name: null,

		/** @property {Nodes} bomSize1Store - store containing list of available product sizes; when BOM sizes are 2d, this will only hold 1st dimension sizes */
		bomSize1Store: null,

		/** @property {Nodes} bomSize2Store - store containing list of 2nd dimension product sizes when BOM sizes are 2d */
		bomSize2Store: null,

		/** @property {SizeSet} bomSizeSet - set of sizes in BOM */
		bomSizeSet: null,

		/** @property {BOMColorCompareData} colorCompareData - object containing data needed to compare BOM */
		colorCompareData: null,

		/** @property {cnl[]} compareNewSizeUrls - sizes in this view that are not in the comparison view */
		compareNewSizeUrls: null,

		/** @property {cnl[]} compareOldSizeUrls - sizes in comparison view that are not in this view */
		compareOldSizeUrls: null,

		// Boolean: if true then compare the style BOM to theme BOM
		compareToTheme: false,

		// Boolean: if true then display color image with color references
		//displayColorImages: false,

		// Boolean: if true then display supplier codes for color and size references
		//displaySupplierCodes: false,

		/** @property {SizeSet} preComparisonActiveSizeSet - active size set without comparison sizes */
		preComparisonActiveSizeSet: null,

		// String[]: store containing list of product suppliers
		//selectedProductSuppliers: null,

		// Actions: actions for DSSection groups
		_bomSectionActions: null,

		// Record<C8AttributeId, Spec>: map of PartMaterial attribute id to mapped MaterialPlanPlacement attribute
		_bomToThemeMap: null,

		/** @property {Action} _editSizeVariationAction - row action to edit size variations */
		_editSizeVariationAction: null,

		/**
		 * Initialize newActionsButtonGroups
		 */
		postCreate: function() {
			this.newActionsButtonGroups = [locale.typePlural("PartMaterial"), locale.resource("Sections")];
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Display compare to theme settings in view footer
		 */
		_buildBody() {
			if (this.inThemeCompareMode()) {
				this.set("messageRight", csiPrefs.optionResource("compareToTheme"));
			}

			return this.inherited(arguments);
		},

		/*
		 * Adds a dependency path of Style to ApparelBOMRevision
		 * Used with AggregateFromStyle, NewPartMaterialOfStyle, and NewPartMaterialFromTemplates
		 * C8-9879 C8-10507
		 * @param {Object} paths
		 */
		_onRootDependencyPaths: function(paths) {
			paths.push(BOM_PARENT_PATH);
			const node = this.cacheView.getNode(this.urlRoot);
			let parent = this.cacheView.getPathNode(node, BOM_PARENT_PATH);
			if (parent?.$Type === PRODUCT_SOURCE) {
				this.addPathsProductSourceContext(paths);
			} else {
				this.addPathsProductContext(parent, paths);
			}
			this.inherited(arguments);
		},

		addPathsProductSourceContext: function(paths) {
			paths.push(COLOR_SOURCES_PATH);
		},

		addPathsProductContext: function(parent, paths) {
			if (parent?.ColorAllocationStatus === "ColorAllocationStatus:Allocation WIP") {
				paths.push(`${BOM_PARENT_PATH}/${COLOR_SOURCES_FROM_PRODUCT_SOURCING}`);
			} else {
				paths.push(COLOR_SOURCES_PATH);
			}
		},

		/*
		 * Checks to see if the New Style and New From Style can be added to a BOM Section. This is true when BOMProductType is either
		 * a style or blank and the BOMProductTypes is empty or has at least one ProductType with IsAssortment Set to true
		 * C8-9879
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		accessActionNewStyle: function(ctx, action) {
			const bomSectionDef = ctx.queryResult && ctx.queryResult.groupValue && ctx.cache.getNode(ctx.queryResult.groupValue);
			const node = this.cacheView.getNode(this.urlRoot);
			const {queryResult} = ctx;

			let product = this.cacheView.getPathNode(node, BOM_PARENT_PATH);

			const isProductSource = product?.$Type === PRODUCT_SOURCE;
			if (isProductSource) {
				product = this.cacheView.getPathNode(product, "Child:__Parent__/Child:__Parent__");

				if (!product) {
					return false;
				}
			}

			// If on a Style that is not IsAssortment, return false for any section
			let isAssortment = ProductType.get(product, IS_ASSORTMENT);
			if (product && !isAssortment) {
				return false;
			}

			// (Blanks) section on an IsAssortment Style, return true
			if (!bomSectionDef && product && isAssortment) {
				return true;
			}

			if (queryResult && queryResult.level && queryResult.level.bAllowNewActionsWhenGrouped) {
				return true;
			}

			if (!bomSectionDef || !bomSectionDef.BOMProductType || bomSectionDef.BOMProductType !== 'ProductType:Style') {
				return false;
			}

			if (bomSectionDef.BOMProductTypes.length === 0) {
				return true;
			}

			return _.some(bomSectionDef.BOMProductTypes, function(url) {
				return ProductType.getByUrl(url, IS_ASSORTMENT);
			});
		},

		/*
		 * If it's a Source BOM and User doesn't have access to product return false
		 * @return {boolean}
		 */
		accessActionNewFromSourceBOM: function() {
			const node = this.cacheView.getNode(this.urlRoot);
			let bomParent = this.cacheView.getPathNode(node, BOM_PARENT_PATH);
			const isProductSource = bomParent?.$Type === PRODUCT_SOURCE;
			let product = this.cacheView.getPathNode(bomParent, "Child:__Parent__");

			if (isProductSource && !product) {
				return false;
			}
			return true;
		},


		/*
		 * If table action or ad hoc section, return true
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		accessActionNewFromStyleBOM: function(ctx, action) {
			const bomSectionDef = ctx.queryResult && ctx.queryResult.groupValue && ctx.cache.getNode(ctx.queryResult.groupValue);
			const dataSheetContainer = this.cacheView.getPathNode(this.urlRoot, BOM_PARENT_PATH);
			const isNewFromStylesBOMInTheme = (dataSheetContainer.$Type === "Theme" && action.m_id === "NewFromThemeStylesBOM");
			const isNewFromThemeBOMInTheme = (dataSheetContainer.$Type === "Theme" && action.m_id === "NewFromThemeBOM");
			return (!bomSectionDef || bomSectionDef.AdHoc || isNewFromStylesBOMInTheme || isNewFromThemeBOMInTheme);
		},

		/*
		 * Allow to Edit Part Material Sizes if the user has access to Actual
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		accessActionEditPartMaterialSizes: function(ctx, action) {
			const pmNode = ctx.getNode();
			const hasAccessToActual = (pmNode != null) ? ctx.cache.getNode(pmNode.Actual) : null;
			return !!hasAccessToActual;
		},

		/*
		* Allow to create new From Style BOM or access Match Colors Action If user has access to product.
		* Trying to fetch product from the SourceBOM if user does not have access to style product will be undefined.
		*/
		accessActionSourceBomPermission: function(ctx, action) {
			const product = this.cacheView.getPathNode(this.urlRoot, BOM_PARENT_PATH);
			const isSourceBom = product?.$Type === "ProductSource";
			const isAllowed = Specification.onAccessActionCompareBOM(ctx, action);

			if (!isAllowed) {
				return false;
			}

			if (isSourceBom) {
				const hasAccessToProduct = !!this.cacheView.getPathNode(product.$URL, BOM_PARENT_PATH);
				const hasAccess = crews.isInGlobalRoles(NEW_STYLE_SOURCE_BOM_ROLES) || crews.isInRoles(NEW_STYLE_SOURCE_BOM_ROLES, ctx.getNode());
				return hasAccess && hasAccessToProduct;
			}

			return true;
		},

		/*
		 * IF the action is under Style and have not Theme or ThemeMaster Subtype distinct to StyleBOM, return false
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		accessActionNewThemeCompare: function(ctx, action) {
			const node = this.cacheView.getNode(this.urlRoot);
			const datasheetContainer = this.cacheView.getPathNode(node, BOM_PARENT_PATH);
			const isAllowed = Specification.onAccessActionCompareBOM(ctx, action);

			if (!isAllowed) {
				return false;
			}


			if (datasheetContainer && datasheetContainer.$Type === "Style") {
				const themeMaster = this.cacheView.getPathNode(datasheetContainer, "Child:Theme/Child:__Parent__");
				if (!themeMaster || config.getSubtypeSetting(themeMaster?.Subtype, "ThemeBOMFeature") !== "ThemeBOMFeature:StyleBOM") {
					return false;
				}
			}

			return true;
		},

		accessActionNewOfMaterial: function(ctx, action) {
			const bomRevision = this.getViewNode();
			const isRequiredSection = this._isRequireSectionForPlacement(bomRevision);

			if (!isRequiredSection) {
				return true;
			}

			const section = ctx?.queryResult?.groupValue;
			const isValid = xml.isValidURL(section);

			return isRequiredSection && isValid;
		},

		postMixInProperties: function() {
			// summary: determine if active product color is specified in browser location (such as when print from DataPackage)
			if (state.get("print") && !state.get("printAdHoc")) {
				let url = session.getLocationValue("BOMColor");
				if (xml.isValidURL(url)) {
					this.bomAvailableColorUrls = [url];
				}

				url = session.getLocationValue("BOMSizes");
				if (xml.isValidURL(url)) {
					this.bomAvailableSizeUrls = url.split(",");
				}

				url = session.getLocationValue("ProductSalesRegion");
				if (xml.isValidURL(url)) {
					this.activeProductSalesRegion = url;
				}
			}

			this.inherited(arguments);
		},

		/**
		 * Return size set for given query result that is not affected by comparison with another view
		 * @param {Result} qr
		 * @return {SizeSet | null}
		 */
		getNonComparisonSizeSetForQueryResult(qr) {
			const {bomActiveSizes, bomActiveSizes2, cacheView, preComparisonActiveSizeSet} = this;
			const sizes = qr.getNode().OnlyForSizes;

			if (sizes && sizes.length) {
				// return only active sizes that apply to result
				sizeSet = new SizeSet(cacheView, sizes);
				return sizeSet.getSubSizeSet(bomActiveSizes, bomActiveSizes2);
			}

			// return sizes only from current view
			return preComparisonActiveSizeSet;
		},

		getPrintProperties: function() {
			// summary: return object containing properties from view necessary to recreate it for printing that are not custom view options
			const params = this.inherited(arguments);
			params.bomAvailableColorUrls = this.bomAvailableColorUrls;
			params.bomAvailableSizeUrls = this.bomAvailableSizeUrls;
			params.bomActiveColors = this.bomActiveColors;
			params.bomActiveSizes = this.bomActiveSizes;
			params.bomActiveSizes2 = this.bomActiveSizes2;
			params.bomSizeSet = this.bomSizeSet;
			params.selectedProductSuppliers = this.selectedProductSuppliers;
			return params;
		},

		/**
		 * Return size set for given query result
		 * @param {Result} qr
		 * @return {SizeSet | null}
		 */
		getSizeSetForQueryResult(qr) {
			const {bomActiveSizeSet, bomActiveSizes, bomActiveSizes2, cacheView, compareData} = this;

			// query result from comparison view, so get size set from that view
			if (qr.bCompareNode) {
				return compareData.compareView.getNonComparisonSizeSetForQueryResult(qr);
			}

			// query result only from current view, so get size set from this view;
			// in a multi-level BOM, it's possible two query results have the same url,
			// and in that case there will be no difference
			const qrComparison = compareData && compareData.getMatchingQueryResult(qr);
			if (!qrComparison || (qrComparison.url === qr.url)) {
				return this.getNonComparisonSizeSetForQueryResult(qr);
			}

			// blend sizes from two views
			const sizes1 = qr.getNode().OnlyForSizes;
			const sizes2 = qrComparison.getNode().OnlyForSizes;

			if (sizes1.length && sizes2.length) {
				// combine two size sets
				const allSizes = _.union(sizes1, sizes2);
				const sizeSet = new SizeSet(cacheView, allSizes);
				return sizeSet.getSubSizeSet(bomActiveSizes, bomActiveSizes2);
			}

			// all sizes apply if either one (or both) does not have OnlyForSizes
			return bomActiveSizeSet;
		},

		/**
		 * Return size set for given query result in context; cache result in context for reuse
		 * @param {Context} ctx
		 * @return {SizeSet | null}
		 */
		getSizeSetForQueryResultInContext(ctx) {
			const {queryResult, sizeSetsPerResult} = ctx;
			const {url} = queryResult;
			let sizeSet = sizeSetsPerResult && sizeSetsPerResult[url];

			if (!sizeSet) {
				ctx.sizeSetsPerResult = ctx.sizeSetsPerResult || {};
				ctx.sizeSetsPerResult[url] = sizeSet = this.getSizeSetForQueryResult(queryResult);
			}

			return sizeSet;
		},

		/** indicate whether or not in the case of theme compare, but not in compare mode(revision, BOM) */
		inThemeCompareMode() {
			return this.compareToTheme && !this.inCompareMode();
		},

		/**
		 * Return true if given size is not in the comparison view but in this view
		 * @param {cnl} sizeUrl
		 * @return {boolean}
		 */
		isNewSize(sizeUrl) {
			return _isSizeInCompareList(sizeUrl, this.compareNewSizeUrls, this.cacheView);
		},

		/**
		 * Return true if given size is in the comparison view and not in this view
		 * @param {cnl} sizeUrl
		 * @return {boolean}
		 */
		isOldSize(sizeUrl) {
			return _isSizeInCompareList(sizeUrl, this.compareOldSizeUrls, this.cacheView);
		},

		/**
		 * Return true if given id is a size variation attribute
		 * @param {string} id
		 * @return {boolean}
		 */
		isSizeColumnHeading(id) {
			return !!SIZE_VARIATION_MAP[id];
		},

		/**
		 * Return size variation heading for given configured heading
		 * @param {Heading} h
		 * @return {Heading}
		 */
		getSizeColumnHeading(h) {
			const id = h.m_id;
			const variationInfo = SIZE_VARIATION_MAP[id];
			if (!variationInfo) {
				return null;
			}

			const key = new Key({
				extendedPath: ExtendedPath.create("PartMaterial", xml.path(variationInfo.composition)),
				id,
			});

			return this.getHeading(key);
		},

		/**
		 * Calculate mapping of BOM sizes to the PartMaterialSizeVariation that applies to it for the given query result
		 * @param {Context} ctx
		 * @param {Result} qr
		 * @param {string} id
		 * @param {boolean} [ignoreLevel] - if true, then get map only for current level, not worrying about previous levels
		 * @return {Object<string,string>} map of BOM Size URL (matrix column) to PartMaterialSizeVariation URL
		 */
		getSizeColumnMap(ctx, qr, id, ignoreLevel) {
			// nothing to do if at root of table or on a group
			if (qr.isRoot() || qr.isGroup()) {
				return null;
			}

			// nothing to do if not a size variation attribute
			const variationInfo = SIZE_VARIATION_MAP[id];
			if (!variationInfo) {
				return null;
			}

			const {attrType, common, composition, nodeType} = variationInfo;
			const hpm = `_HASH_${id}_${!!ignoreLevel}`;

			// return if column map is already cached
			const hpmMap = ctx[hpm] = ctx[hpm] || {};
			let columnMap = hpmMap[qr.id];
			if (columnMap) {
				return columnMap;
			}

			// create map of BOM Sizes that apply for this PartMaterial
			const cache = ctx.cache;
			const pm = qr.getNode();
			const pmSizes = cache.getNodes(pm[composition]);
			const onlyForSizes = pm.OnlyForSizes;
			const bOnlyForSizes = !!onlyForSizes.length;
			let bomSizeMap;

			// matrix is all hashed out if no variations (no OnlyForSizes and not OKVariationByBOMSize)
			if (bOnlyForSizes || pm.OKVariationByBOMSize) {
				if (bOnlyForSizes) {
					bomSizeMap = csi.hashArray(onlyForSizes, null, Boolean);
				}

				// determine mapping from BOM Size to PartMaterialSizeVariation
				const bomRevision = cache.getNode(pm.__Parent__);
				const allSizeMap = csi.hashArray(bomRevision.BOMProductSizes, null, Boolean);
				const obj = {};

				_.each(pmSizes, function(pmSize) {
					_.each(pmSize[SIZE_VARIATION_BOM_SIZES], function(urlBomSize) {
						delete allSizeMap[urlBomSize];

						if (!bomSizeMap || bomSizeMap[urlBomSize]) {
							obj[urlBomSize] = pmSize.$URL;
						}
					});
				});

				// if there are sizes on the BOM that are not referenced by a PartMaterialSizeVariation node, then create a client one to hold the common value
				const missingSizeValues = _.keys(allSizeMap);
				if (missingSizeValues.length) {
					const pmSizeCommon = cache.createClientNode(null, nodeType);
					xml.nodeCreateClientAttr(pmSizeCommon, id, attrType, pm[common]);
					xml.nodeCreateClientAttr(pmSizeCommon, SIZE_VARIATION_BOM_SIZES, xml.ATTR_REFSET, missingSizeValues);
					xml.nodeCreateClientAttr(pmSizeCommon, xml.ATTR_PARENT, xml.ATTR_REF, pm.$URL);

					if (common === "CommonSize") {
						xml.nodeCreateClientAttr(pmSizeCommon, "SizeAvailable", xml.ATTR_BOOLEAN, pm.CommonSizeAvailable);
					}

					_.each(missingSizeValues, function(urlBomSize) {
						if (!bomSizeMap || bomSizeMap[urlBomSize]) {
							obj[urlBomSize] = pmSizeCommon.$URL;
						}
					});
				}

				if (ignoreLevel || qr.level.bMain || partMaterialIsSpecial(cache, qr.parentResult.getNode())) {
					// if at main level, then PartMaterialSizeVariation nodes will directly reference BOM size
					columnMap = obj;
				} else {
					// if not at main level, then PartMaterialSizeVariation nodes will reference a ProductSize selected in parent PartMaterial, rather than the column's BOM Size URL
					const parentMap = this.getSizeColumnMap(ctx, qr.parentResult, SIZE_PARAMS.attr);

					columnMap = {};

					_.each(parentMap, function(pmSizeUrl, bomSizeUrl) {
						let pmSize = cache.getNode(pmSizeUrl);
						if (pmSize) {
							let sizeUrl = pmSize[SIZE_PARAMS.attr];
							columnMap[bomSizeUrl] = obj[sizeUrl];
						}
					});
				}
			} else {
				// empty matrix - no variations
				columnMap = {};
			}

			// cache for later
			hpmMap[qr.id] = columnMap;

			return columnMap;
		},

		onChangeCustomView: function() {
			// summary: reset active product colors and sizes when custom view is changed
			this.set("bomActiveColors", null);
			this.set("bomColorStore", null);
			this.set("bomSizeSet", null);
			this.set("bomActiveSizes", null);
			this.set("bomActiveSizes2", null);
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Determine if able to edit size variations
		 */
		onGenerateResultRow(ctx) {
			const {_editSizeVariationAction} = this;
			ctx.setParam(ACTION_EDIT_SIZE_VARIATIONS, _editSizeVariationAction ? _editSizeVariationAction.hasAccess(ctx) : false);
		},

		/**
		 * @override
		 * Product alternatives do not apply to materials
		 */
		onViewResolveKeysToSkip(skipSet) {
			if (this.getViewNodeType() === "MaterialBOMRevision") {
				skipSet.add(Key.untypedKeyFromParts({
					id: "OnlyForProductAlternatives",
				}));
			}
		},

		/**
		 * @override
		 * Initialize properties for view
		 */
		onViewStartBuild: function() {
			if (!this.bomSizeSet) {
				this.initializeBomSizeSet();
			}

			this._initializeProductSuppliers();
			this.inherited(arguments);
		},

		/**
		 * Add behavior to cell to enable quick access to edit size variation action
		 * @param {Context} ctx
		 * @param {DOMNode} td
		 * @param {string} initialVariationNodeType
		 */
		postRenderSizeVariationCell(ctx, td, initialVariationNodeType) {
			const qr = ctx.queryResult;
			if ((qr.level.bMain || partMaterialIsSpecial(ctx.cache, qr.parentResult.getNode())) && ctx.getParam(ACTION_EDIT_SIZE_VARIATIONS)) {
				const partMaterialUrl = ctx.getNodeParentURL();

				td.classList.add("csi-table-cell-hover-icon");
				csiHeading.addHeadingNodeEditableStyling(td);
				td.onclick = () => {
					csiAction.invoke(this, td, this._editSizeVariationAction, {
						initialVariationNodeType,
						m_urlNode: partMaterialUrl,
					})
				};

				html.create("div", {className: "csi-table-hover-icon"}, td);
			}
		},

		/**
		 * Initialize size set for view
		 */
		initializeBomSizeSet() {
			const rev = this.getViewNode();
			if (!rev) {
				return;
			}

			const sizeUrls = this._getAvailableBomSizeUrls(rev);
			const sizeSet = sizeUrls.length ? new SizeSet(this.cacheView, sizeUrls) : null;
			this.set("bomSizeSet", sizeSet);
		},

		/**
		 * Remove color from active set if removed from the BOM
		 * @param {cnl} removedColorUrl
		 */
		removeBomColor(removedColorUrl) {
			// if a color was removed, then ensure it is no longer in active list
			const colors = this.bomActiveColors?.filter((url) => url !== removedColorUrl);
			this.set("bomActiveColors", colors);
		},

		/**
		 * Initialize product suppliers for view if matrix is exposed
		 */
		_initializeProductSuppliers() {
			if (this.selectedProductSuppliers !== undefined) {
				return;
			}

			const rev = this.getViewNode();
			const plugin = this.getPlugin("SelectProductSuppliers");

			// it could be under the node other than BOM, for example, supplier quote
			if (!rev || !plugin) {
				return;
			}

			const bom = this.cacheView.get(rev.__Parent__);
			const exposeBOMSupplierAttributes = config.getNodeSetting(bom, EXPOSE_BOM_SUPPLIER_ATTR);

			if (!exposeBOMSupplierAttributes || bom.IsSampleBOM) {
				return;
			}

			const locationSupplier = session.getLocationValue("ProductSupplier");
			const {store} = plugin;
			let selected;

			if (bom.ForProductSource !== csi.EMPTY_URL) {
				selected = [bom.ForProductSource];
			} else if (locationSupplier) {
				selected = [locationSupplier];
			} else {
				selected = storage.get(STORAGE_SELECTED_SUPPLIERS) || [];
			}

			// validate suppliers are in store
			selected = selected.filter((url) => store.get(url));

			this.set("selectedProductSuppliers", selected);

			if (selected) {
				this.watch("selectedProductSuppliers", (name, oldValue, newValue) => {
					storage.set(STORAGE_SELECTED_SUPPLIERS, newValue);
				});
			}
		},

		_addProductSalesRegionQuery: function(/*Query*/q) {
			// summary: add filter if specified in page location
			const activeProductSalesRegion = this.activeProductSalesRegion;

			if (xml.isValidURL(activeProductSalesRegion)) {
				let salesRegions = [activeProductSalesRegion];

				const activeProductSalesRegionNode = this.getCache().getNode(activeProductSalesRegion);
				if (activeProductSalesRegionNode && activeProductSalesRegionNode.SecondaryProductSalesRegions) {
					salesRegions = salesRegions.concat(activeProductSalesRegionNode.SecondaryProductSalesRegions);
				}

				_.each(salesRegions, function(region) {
					this.RefAttr("OnlyForProductSalesRegions", xml.OP_EQUALS, region)
						.RefAttr("OnlyForProductSalesRegions", xml.OP_NULL);
				}, q.Or());
			}
		},

		_addProductSizeQuery: function(/*Query*/q) {
			// summary: add filter if specified in page location
			const sizeSet = this.bomActiveSizeSet;
			if (!sizeSet) {
				return;
			}

			const onlyForSizesAttr = "OnlyForSizes";
			const size1Urls = this.bomActiveSizes;
			const size2Urls = this.bomActiveSizes2;

			function startOrCondition() {
				return q.Or().RefAttr(onlyForSizesAttr, xml.OP_NULL);
			}

			if (sizeSet.has2dSizes()) {
				const path = xml.path(onlyForSizesAttr);

				if (!_.isEmpty(size1Urls)) {
					_.each(size1Urls, function(url) {
						this.RefAttr("Dimension1Size", xml.OP_EQUALS, url, path);
					}, startOrCondition());
				}

				if (!_.isEmpty(size2Urls)) {
					_.each(size2Urls, function(url) {
						this.RefAttr("Dimension2Size", xml.OP_EQUALS, url, path);
					}, startOrCondition());
				}
			} else if (!_.isEmpty(size1Urls)) {
				_.each(size1Urls, function(url) {
					this.RefAttr(onlyForSizesAttr, xml.OP_EQUALS, url);
				}, startOrCondition());
			}
		},

		/**
		 * @override
		 * Filter out PartMaterials that do not apply to selected product alternative
		 * and taking into account the filters on CV - OnlyForColors
		 */
		onBuildColumns: function(ctx, dfds) {
			const store = this.get("store");

			// the store can be null when on the Supplier Quote Presentation tab and no QuoteBOM is selected
			if (!store) {
				return;
			}

			const q = store.filterQuery || new Query();

			// filter out PartMaterials that do not apply to selected product alternative
			// TODO: Now that entire BOM is queried from the server, it is possible this logic could move to the client
			// to reduce queries to server when changing switchboard values
			this._addProductAlternativeQuery(q);
			this._addProductSalesRegionQuery(q);
			this._addProductSizeQuery(q);

			// filter out PartMaterials which are hidden for all selected colors
			const {bomActiveColors} = this;
			const storeColorUrls = !_.isEmpty(this.bomColorStore) ? this.bomColorStore.resultUrls : null;
			const resultUrls = _.isEmpty(bomActiveColors) ? storeColorUrls : bomActiveColors;

			if (!_.isEmpty(resultUrls)) {
				const qOr = q.Or().RefAttr("OnlyForColors", xml.OP_NULL);
				for (const url of resultUrls) {
					qOr.RefAttr("OnlyForColors", xml.OP_EQUALS, url);
				}
			}

			if (!q.isEmpty()) {
				store.filterQuery = q;
			}
		},

		/**
		 * @override
		 * Enable new actions per group when grouping by product type if api used
		 */
		onCreateQueryLevel(ql) {
			if (this.isProductTypeGroupWithNewActions(ql)) {
				ql.bAllowNewActionsWhenGrouped = true;
			}
			this.inherited(arguments);
		},

		_createQueryLevel: function(/*Object*/params, /*Boolean*/bRoot) {
			// summary: add filter if specified in page location
			const ql = this.inherited(arguments);

			if (this.selectedProductSuppliers && this.selectedProductSuppliers.length) {
				// Dependency needed to cache the BOMSupplierAttribute's parent ProductSource node. See _addToBomSupplierAttributes function
				ql.addDependencyPaths([xml.path("BOMSupplierAttributes", xml.ATTR_PARENT)]);
				ql.storeOptions = {
					onResponse: lang.hitch(this, this._extractBOMSupplierAttributes)
				};
			}

			if (ql.bMain) {
				// store size variation action for quick access
				if (ql.actions) {
					this._editSizeVariationAction = ql.actions.getAction(ACTION_EDIT_SIZE_VARIATIONS);
				}

				// if comparing to Theme, then need to add dependency paths for mapped attributes
				if (this.inThemeCompareMode()) {
					// add dependency paths for MPP attributes whose corresponding PM attribute is in the view
					const mppSpecs = _.values(this._getBOMToThemeMap());
					const specs = new Specs(mppSpecs);

					ql.addDependencyPaths(collectSpecDependencyPaths(specs));

					// add dependency paths needed to display PartMaterial's CommonColor corresponding MaterialPlanPlacement's Color and the default Color Images
					const pathToMPPColor = xml.path("FromMaterialPlanPlacement", "MPPColor");
					const pathToMPPColorColorSpec = xml.path(pathToMPPColor, "ColorSpecification");
					const pathToMPPColorSpec = xml.path("FromMaterialPlanPlacement", "MPPColorSpec");
					ql.addDependencyPaths([pathToMPPColor,
						csiImage.getDefaultImagePath(pathToMPPColor),
						csiImage.getDefaultImagePath(pathToMPPColorColorSpec),
						csiImage.getDefaultImagePath(pathToMPPColorSpec)]);
				}
			}

			if (ql.groupSpec && (ql.groupSpec.heading.m_id === "DSSection") && this.isGroupBySection(true)) {
				ql.actions = this._bomSectionActions;

				ql.childLevel.actionFilterFunc = function(ctx, qrParent, action) {
					let result = true;
					const groupUrl = qrParent.groupValue;
					if (qrParent && xml.isValidURL(groupUrl)) {
						const bomSecDef = ctx.cache.getNode(groupUrl);

						if (bomSecDef && bomSecDef.AdHoc === false) {
							const productType = xml.enumGetText(bomSecDef.PlacementProductType);

							if (productType === "Material") {
								// to exclude an action, add it to the switch below
								switch (action.m_id) {
									case "NewPartMaterialOfStyle":
									case "AggregateFromStyle":
										result = false;
										break;
									default:
										break;
								}
							} else if (productType === "Style") {
								// to exclude an action, add it to the switch below
								switch (action.m_id) {
									case "NewPartMaterialOfMaterial":
									case "AggregateFromMaterial":
									case "NewSpecial":
									case "AggregateFromTheme":
									case "NewPartMaterialFromTemplates":
										result = false;
										break;
									default:
										break;
								}
							}
						}
					}

					return result;
				};

				let viewNode = this.getViewNode();
				if (viewNode && (viewNode.$Type === "ApparelBOMRevision" || viewNode.$Type === "MaterialBOMRevision")) {
					let sections = viewNode.AllSections;
					if (this.bPopup) {
						const sectionUrlSet = new Set();
						viewNode.Items.forEach((item) => sectionUrlSet.add(item.DSSection));
						sections = sections.filter((url) => sectionUrlSet.has(url));
					}
					ql.groupDefaultsStore = this._getGroupDefaultsStore(sections, this.cacheView);
				}
			}
			return ql;
		},

		_extractBOMSupplierAttributes: function(res) {
			if (!res.data || !res.data.NODES || res.data.NODES.Nodes)
				return;

			// Mapping of Supplier & PartMaterial sysID to BOMSupplierAttributes
			// putting it on the this.cacheView allow us to share between views as cache
			if (!this.cacheView.bomSupplierAttributes) {
				this.cacheView.bomSupplierAttributes = {};
			}

			this._addToBomSupplierAttributes(res.getNodesOfType("BOMSupplierAttributes"));
		},

		_addToBomSupplierAttributes: function(/*Array*/nodesToAdd) {
			// summary: takes an array of BOM Supplier Attribute nodes and maps them to their corresponding part material sysID
			_.each(nodesToAdd, function(node) {
				const productSource = this.cacheView.getNode(node.__Parent__);
				_.each(node.PartMaterials, function(pm) {
					this.cacheView.bomSupplierAttributes[productSource.Supplier + ";" + pm] = node;
				}, this);
			}, this);
		},

		/**
		 * When comparing to Theme and a difference is detected in value, then render extra cell
		 */
		_generateResultCell: function(ctx, qr, spec, tr, td, nColumn, bFirstCell) {
			PlacementTable.prototype._generateResultCell.call(this, ctx, qr, spec, tr, td, nColumn, bFirstCell);

			// in compare mode, there will be an extra row to indicate this, no need to render extra cell to do that
			// need to rework on this in a separate story
			if (this.inThemeCompareMode()) {
				const h = spec.heading;
				const pm = ctx.getNode();
				const mpp = ctx.cache.getNode(pm.FromMaterialPlanPlacement);
				const mppSpec = this._getBOMToThemeMap()[h.m_id];

				if (pm && mpp && mppSpec) {
					// Special handling for PartMaterial.CommonColor - it maps to either MPPColorSpec or MPPColor on MaterialPlanPlacement
					const mppValue = (h.m_id === "CommonColor" && !xml.isValidURL(mpp.MPPColorSpec)) ? mpp.MPPColor : mpp[mppSpec.heading.m_id];

					if (pm[h.m_id] !== mppValue) {
						const tdCompare = DataSheetTable.prototype._generateResultCell.call(this, ctx, qr, mppSpec, tr, null, nColumn, bFirstCell);
						domClass.add(tdCompare, "csi-secondary-text");
					}
				}
			}

			return td;
		},

		/**
		 * Return map from PartMaterial attributes to Spec for mapped MaterialPlanPlacement attribute
		 */
		_getBOMToThemeMap() {
			const ctx = this.getContext();
			let map = this._bomToThemeMap;

			if (!map) {
				map = this._bomToThemeMap = {};

				// Add OOTB attributes
				// string
				_addBomToThemeMapping(map, ctx, xml.ATTR_NAME);
				// double
				_addBomToThemeMapping(map, ctx, "QtyDefault", "QtyCommon");
				// boolean
				_addBomToThemeMapping(map, ctx, "MainMaterial");
				// ref
				_addBomToThemeMapping(map, ctx, "Actual");
				_addBomToThemeMapping(map, ctx, "CommonColor", "MPPColorSpec");  // TODO: could be MPPColor
				_addBomToThemeMapping(map, ctx, "CommonSize", "MPPSize");
				_addBomToThemeMapping(map, ctx, "BOMLineQuote", "MPPQuote");

				// Add custom attributes
				const themeToPartMaterialCustomAttributeMap = config.getClassSetting("MaterialPlanPlacement", "BOMLineAttributeMap");

				_.each(themeToPartMaterialCustomAttributeMap, function(value, key) {
					_addBomToThemeMapping(map, ctx, value, key);
				});
			}

			return map;
		},

		_resolveView: function() {
			// summary: view pre-processing
			const {defView} = this;

			// prepare actions for DSSection group if user has admin rights on revision
			// TODO: finish configuration of actions, which may include changing access rights check here
			const deleteBOMRequest = lang.mixin(new Request(), {
				m_module: "Specification",
				m_operation: "DeleteBOMSectionDefinitions"
			});
			const pushBOMRequest = lang.mixin(new Request(), {
				m_module: "Specification",
				m_operation: "PushBOMSection"
			});

			if (this.defView.shouldAllowActions()) {
				this._bomSectionActions = _.extend(new Actions(), {
					m_actions: [
						defView._addViewAction(ACTION_EDIT, {
							classController: EditSectionController,
							m_viewRef: lang.mixin(new ViewRef(), {
								m_id: "AdHocForm"
							}),
							m_accessAction: "_accessActionEditSection"
						}),
						defView._addViewAction(ACTION_PUSH, {
							classController: BOMSectionPushController,
							m_accessAction: "_accessActionPushSection",
							m_request: pushBOMRequest
						}),
						defView._addViewAction(ACTION_DELETE, {
							classController: DeleteSectionController,
							m_accessAction: "_accessActionDeleteSection",
							m_request: deleteBOMRequest
						})
					]
				});

				if (!featureTogglesStore.ENABLE_UPDATE_WEIGHT_PER_BOM_QTY_ACTION) {
					this.defView.defToolbar?.menuActions?.removeAction(ACTION_UPDATE_WEIGHT_PER_BOM_QTY);
				}
			}

			this.inherited(arguments);
		},

		/**
		 * Table widget won't reset Levels if there's no Matrix headings in the table.
		 * When user has not selected any options in the Product Suppliers dropdown on the switchboard, and then makes the first selection,
		 * postAction won't reset Levels because there are no Matrix headings. This is an issue, so we need to override this method to reset Levels
		 * if we have at least one ProductSuppliers selected.
		 *
		 * @return {Boolean}
		 */
		hasMatrix() {
			return this.inherited(arguments) || !!this.selectedProductSuppliers?.length;
		},

		/**
		 * If in compareToTheme mode, and part material is linked to theme, then redraw row for inline actions
		 */
		preAction: function(ac) {
			const node = ac.getNode();
			const h = ac.heading;
			const bMapped = !!this._getBOMToThemeMap()[h?.m_id];
			const bFromTheme = xml.isValidURL(node?.FromMaterialPlanPlacement);
			const bInline = dom.isDescendant(ac.m_element, ac.refNode);

			if (this.inThemeCompareMode() && bInline && bFromTheme && bMapped) {
				ac.bRefreshNode = true;
			}

			this.inherited(arguments);
		},

		/**
		 * Return SizeSet combining sizes of comparison view with this view
		 * @param {cnl[]} sizeUrls
		 * @return {SizeSet}
		 */
		_getActiveSizeSetWithComparisonSizes(sizeUrls) {
			const {
				bomActiveSizes,
				bomActiveSizes2,
				cacheView,
				compareData: {compareView: {bomSizeSet}}
			} = this;

			if (bomSizeSet) {
				const sizeSet = bomSizeSet.getSubSizeSet(bomActiveSizes, bomActiveSizes2);
				sizeUrls = _.union(sizeUrls, sizeSet.sizeUrls);
			}

			return new SizeSet(cacheView, sizeUrls, true);
		},

		/**
		 * Return available sizes in given BOM
		 * @param {C8Node} revision
		 * @return {cnl[]}
		 */
		_getAvailableBomSizeUrls(revision) {
			const {bomAvailableSizeUrls, cacheView} = this;
			let sizeUrls = revision.BOMProductSizes;

			// narrow set of sizes based on what is available
			if (bomAvailableSizeUrls) {
				sizeUrls = _.intersection(bomAvailableSizeUrls, sizeUrls);
			}

			// apply custom view filter
			const sizeKey = def.getHeadingKey(null, "PartMaterialSizes");
			const ql = this.createMatrixQueryLevel(sizeKey);

			if (ql) {
				const q = new Query();
				ql.applyLevelQuery(q);
				const filteredSizes = QueryProcessor.applyQueryToNodes(q, cacheView.getNodes(sizeUrls));
				sizeUrls = _.pluck(filteredSizes, "$URL");
			}

			return sizeUrls;
		},

		/**
		 * @override
		 * Return custom description text for part materials with no result;
		 */
		_getNoResultDescription: function(hasColumnFilter, hasNewAction) {
			const bomRevision = this.getViewNode();
			if (bomRevision?.AllSections.length === 0 && this._hasPlacementSectionFilter(bomRevision)) {
				return locale.resource("ViewPlaceholderDescriptionNoResultForPartMaterialsRequiredSection");
			}

			if (!hasColumnFilter && hasNewAction) {
				return locale.resource("ViewPlaceholderDescriptionNoResultForPartMaterials");
			}

			return this.inherited(arguments);
		},

		/**
		 * Update active size set when selection of 1st dimension sizes changes
		 * @param {string[]} urls
		 */
		_setBomActiveSizesAttr: function(urls) {
			this._set("bomActiveSizes", urls);
			this._updateBomActiveSetSet();
		},

		/**
		 * Update active size set when selection of 2nd dimension sizes changes
		 * @param {string[]} urls
		 */
		_setBomActiveSizes2Attr: function(urls) {
			this._set("bomActiveSizes2", urls);
			this._updateBomActiveSetSet();
		},

		/**
		 * Assign labels to switchboards based on 2d size dimensions
		 * @param {SizeSet} sizeSet
		 */
		_setBomSizeSetAttr: function(sizeSet) {
			this._set("bomSizeSet", sizeSet);

			if (sizeSet?.has2dSizes()) {
				this.set("bomSize1Name", sizeSet.dim1Name);
				this.set("bomSize2Name", sizeSet.dim2Name);
			}

			this._updateBomActiveSetSet();

			// update these stores last so that active sizes are correct before updated switchboards
			this.set("bomSize1Store", sizeSet?.getSize1Store());
			this.set("bomSize2Store", sizeSet?.getSize2Store());
		},

		/**
		 * @override
		 * Clear comparison properties when exiting compare mode
		 */
		_setCompareDataAttr(compareData) {
			this.inherited(arguments);
			this._updateBomActiveSetSet();
		},

		/**
		 * Calculate active size set from selected dimension sizes
		 */
		_updateBomActiveSetSet() {
			const {bomSizeSet, bomActiveSizes, bomActiveSizes2, compareData} = this;
			const newSizeUrls = [];
			const oldSizeUrls = [];
			let activeSizes;
			let activeSizes2;
			let activeSizeSet;
			let originalSizeSet;

			if (bomSizeSet) {
				// validate active sizes against sizes in set - it's possible that one has been removed from Dimensions tab
				activeSizes = bomActiveSizes?.filter((url) => bomSizeSet.has1dSize(url));
				activeSizes2 = bomActiveSizes2?.filter((url) => bomSizeSet.has2dSize(url));

				// calculate active size set
				originalSizeSet = activeSizeSet = bomSizeSet.getSubSizeSet(activeSizes, activeSizes2);

				if (compareData) {
					// when comparing, include sizes from the comparison view as well
					const {bomActiveSizeSet: compareActiveSizeSet} = compareData.compareView;
					const {dim1SizeUrls, dim2SizeUrls, sizeUrls} = activeSizeSet;

					const diff = (a1, a2) => {
						const newDiff = _.difference(a1, a2);
						newSizeUrls.push(...newDiff);

						const oldDiff = _.difference(a2, a1);
						oldSizeUrls.push(...oldDiff);
					};

					originalSizeSet = activeSizeSet;
					activeSizeSet = this._getActiveSizeSetWithComparisonSizes(sizeUrls);

					// it's possible compare BOM has no size set
					diff(sizeUrls, compareActiveSizeSet ? compareActiveSizeSet.sizeUrls : []);

					// if there are 2d sizes, then need to include the 1d sizes in the old and new lists
					if (activeSizeSet.has2dSizes()) {
						diff(dim1SizeUrls, compareActiveSizeSet ? compareActiveSizeSet.dim1SizeUrls : []);
						diff(dim2SizeUrls, compareActiveSizeSet ? compareActiveSizeSet.dim2SizeUrls : []);
					}
				}
			}

			// set these using internal set function so as not to cause an infinite loop
			this._set("bomActiveSizes", activeSizes);
			this._set("bomActiveSizes2", activeSizes2);

			this.set("bomActiveSizeSet", activeSizeSet);
			this.set("compareNewSizeUrls", newSizeUrls);
			this.set("compareOldSizeUrls", oldSizeUrls);
			this.set("preComparisonActiveSizeSet", originalSizeSet);
		}
	});
});

